import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Leaf, Truck, HeartHandshake, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";

const VALUES = [
  { icon: ShieldCheck, title: "Quality & consistency", body: "Trusted brands and quality product lines you can stand behind, order after order." },
  { icon: Truck, title: "Reliable supply", body: "Dependable stock and fast, nationwide dispatch across New Zealand." },
  { icon: Leaf, title: "Sustainability", body: "Recyclable, recycled-content and compostable options across the range." },
  { icon: HeartHandshake, title: "Real partnership", body: "Expert advice and account support from people who know your business." },
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-28 pb-20 md:pb-28 bg-vr-bg">
      <SEO
        title="About VR Packaging & Hygiene | NZ Wholesale Supplier"
        description="Trusted New Zealand supplier of wholesale packaging, hygiene, cleaning and safety products. Nationwide dispatch, wholesale pricing, one dependable partner for every category."
        path="/about"
      />
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
          <span className="text-xs tracking-[0.2em] uppercase text-vr-brand font-semibold">About us</span>
          <h1 className="mt-4 font-display font-extrabold tracking-tight leading-[1.02] text-4xl md:text-6xl text-vr-ink">
            A trusted name in packaging & hygiene.
          </h1>
          <p className="mt-6 text-lg text-vr-dim leading-relaxed">
            VR Packaging & Hygiene supplies businesses across New Zealand with a complete range of
            packaging, hygiene, cleaning and safety products. From hospitality and healthcare to
            manufacturing and logistics, we help operations of every size stay clean, compliant and
            well-stocked — all from a single, dependable supplier.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-14 relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-vr-navy via-[#12405b] to-vr-brand aspect-[16/7] shadow-xl"
        >
          <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-vr-brand/40 blur-3xl" />
          <div className="absolute -bottom-14 -left-12 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.10]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "52px 52px",
            }}
          />
          <div className="relative h-full flex flex-col justify-center px-8 md:px-14">
            <h2 className="font-display font-extrabold tracking-tight text-white text-2xl md:text-4xl max-w-2xl leading-tight">
              One partner for packaging, hygiene, cleaning &amp; safety — right across New Zealand.
            </h2>
            <p className="mt-4 text-white/70 max-w-xl">Wide product range · Nationwide delivery · Wholesale pricing</p>
          </div>
        </motion.div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-vr-border bg-white p-6"
            >
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-vr-brand/10 text-vr-brand mb-5"><v.icon size={22} /></span>
              <h3 className="font-display font-bold text-lg text-vr-ink mb-2">{v.title}</h3>
              <p className="text-sm text-vr-dim leading-relaxed">{v.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 rounded-3xl bg-vr-brand p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-extrabold text-2xl md:text-4xl text-white tracking-tight">Ready to simplify your supply?</h2>
            <p className="mt-2 text-white/80">Get a tailored quote across every category you need.</p>
          </div>
          <Link to="/contact" data-testid="about-cta" className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-white text-vr-brand font-semibold hover:bg-vr-ink hover:text-white transition-colors whitespace-nowrap">
            Contact us <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </main>
  );
}
